import React, { useCallback } from "react";
import { includes, map } from "lodash";
import { Facet } from "@linkfluence/models/facet";
import Subtitle from "@radarly/components/cards/card/subtitle";
import { useCustomFields } from "@radarly/core/custom-field/use-cases";
import { useCurrentProject } from "@radarly/core/project/use-cases";
import { TermCard } from "@radarly/models/cards";
import { changeTermSelectionMethod } from "@radarly/models/cards/form";
import { getDateFromClause } from "@radarly/models/clause/getter";
import { getPostSortFromMetric } from "@radarly/models/posts";
import { getTermFromAggs } from "@radarly/models/search";
import Date from "@radarly/shared/cards/subtitle/date";
import Metric from "@radarly/shared/cards/subtitle/metric";
import Tile from "@radarly/shared/cards/tile";
import Widget from "@radarly/shared/widgets/term";
import { getFocusClause } from "../clause";
import { useFacets } from "../facets";
import { useFragments } from "../fragment";
import { useGenerateCSV } from "../hooks/useGenerateCSV";
import { CardProps } from "../model";

const RadarlyTermCards = ({
  card,
  clause,
  from,
  to,
  height,
  openPostsPanel,
  timezone,
  forceReload,
  nowDate,
  isEdition,
  setDataviz,
}: CardProps<TermCard>) => {
  const project = useCurrentProject();
  const fragments = useFragments(
    [card.fragments[0], card.fragments[1]],
    clause,
    from,
    to,
    timezone
  );
  const fragment = fragments[0];
  const evolutionFragment = fragments[1];
  const term = getTermFromAggs(fragment.request.aggs) as Facet;
  const fields = useCustomFields();
  const metric = fragment.request.aggs[term].metrics[0];
  const facet = useFacets(term, fragment.request.query);

  const title = card.title;
  const { onGenerateCSV } = useGenerateCSV(
    card.title,
    fragment,
    evolutionFragment
  );

  const onClickItem = useCallback(
    (ids: string | number | Array<string | number>) => {
      const { from, to } = getDateFromClause(fragment.request.query);
      const normalizedTerm = changeTermSelectionMethod(term, "frequency");
      openPostsPanel({
        clause: fragment.request.query,
        period: {
          from,
          to,
        },
        title,
        timezone,
        focusClause: getFocusClause(
          normalizedTerm,
          ids,
          includes(map(fields, "label"), term)
        ),
        sortBy: getPostSortFromMetric(fragment.request.aggs[term].metrics[0]),
      });
    },
    [
      fragment.request.query,
      fragment.request.aggs,
      term,
      openPostsPanel,
      title,
      timezone,
      fields,
    ]
  );
  return (
    <Tile
      title={title}
      height={height}
      subtitle={
        <Subtitle
          meta={[
            <Date fragment={fragment} timezone={timezone} nowDate={nowDate} />,
            <Metric metric={metric} />,
          ]}
        />
      }
      clause={fragment.request.query}
      onGenerateCSV={onGenerateCSV}
      isPNG
    >
      <Widget
        fragment={fragment}
        evolutionFragment={evolutionFragment}
        project={project.id}
        onClickItem={openPostsPanel ? onClickItem : null}
        facet={facet}
        forceReload={forceReload}
        isEdition={isEdition}
        setDataviz={setDataviz}
        card={card}
      />
    </Tile>
  );
};

export default RadarlyTermCards;
